import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import React, { useCallback } from 'react'
import { PILLAR_NS_MAP, PillarPage } from 'src/constants/searchPages'
import posthog from 'src/utils/pixels/posthog'

interface PillarDisambiguationProps {
  /** Pillar of the page rendering this section. The section only renders on
   *  SelfStorage and Warehouse pillars — those are the two that compete for
   *  the ambiguous "renta de bodegas" / "bodegas en renta" search intent.
   *  Parking and Industrial always return null. */
  pillar: PillarPage
  /** Optional city slug for the current page (e.g. `monterrey`). When
   *  provided, the cross-link preserves city context. Hood is intentionally
   *  dropped — Warehouse has no SSG hood pages (see PRERENDER_HOOD_PILLARS),
   *  so preserving it would risk 404s. */
  currentCity?: string
  /** City/hood pages set this: the two-tile strip collapses to a
   *  one-line link so the first listing card stays inside the first
   *  viewport (the strip cost ~200px of it on the paid MTY lander). The pillar
   *  landing keeps the full strip on every breakpoint. */
  compact?: boolean
}

type TileSide = 'mini' | 'warehouse'

const PILLAR_BY_SIDE: Record<TileSide, PillarPage> = {
  mini: PillarPage.SelfStorage,
  warehouse: PillarPage.Warehouse
}

const TARGETS: Partial<Record<PillarPage, PillarPage>> = {
  [PillarPage.SelfStorage]: PillarPage.Warehouse,
  [PillarPage.Warehouse]: PillarPage.SelfStorage
}

/**
 * Compact cross-pillar disambiguation strip.
 *
 * "Renta de bodegas" is genuinely ambiguous between Mini Bodegas (3–50 m²,
 * personal storage) and Bodegas Comerciales (50 m² and up, business
 * inventory). This strip lets visitors who landed on the wrong product jump
 * to the right one without losing the funnel. Sits below the hero on the
 * pillar landing and below the H1 strip on city/hood pages.
 *
 * Trimmed-down version: single eyebrow row + two horizontal tiles (size +
 * label on the left, arrow or "Estás aquí" badge on the right). No H2, no
 * subtitle, no descriptions — the strip should read as a related-link tip,
 * not a section, so the listings carousel stays close to the hero.
 */
const PillarDisambiguation = ({
  pillar,
  currentCity,
  compact = false
}: PillarDisambiguationProps) => {
  const target = TARGETS[pillar]
  const { t } = useTranslation(PILLAR_NS_MAP[pillar])

  // Fires `pillar_disambiguation_click` so we can attribute wrong-pillar
  // landings back to the upstream campaign / keyword / landing surface that
  // sent the visitor here. The visitor explicitly told us "I'm on the wrong
  // page, take me there" by clicking — that's the cleanest mis-routing
  // signal we have. PostHog's sticky super-properties (`utm_*`,
  // `classified_source`, `landing_surface_first`, `hsa_kw`) ride on the
  // event automatically — no need to read them here.
  const handleClick = useCallback(
    (toPillar: PillarPage) => {
      posthog.event('pillar_disambiguation_click', {
        from_pillar: pillar,
        to_pillar: toPillar,
        current_city: currentCity ?? null,
        has_city_context: Boolean(currentCity)
      })
    },
    [pillar, currentCity]
  )

  if (!target) return null

  const otherHref = currentCity ? `/${target}/${currentCity}` : `/${target}`
  const otherSide: TileSide =
    target === PillarPage.Warehouse ? 'warehouse' : 'mini'
  const otherSizeKey =
    otherSide === 'mini'
      ? 'PILLAR_DISAMBIGUATION.MINI.SIZE'
      : 'PILLAR_DISAMBIGUATION.WAREHOUSE.SIZE'
  const otherLabelKey =
    otherSide === 'mini'
      ? 'PILLAR_DISAMBIGUATION.MINI.LABEL'
      : 'PILLAR_DISAMBIGUATION.WAREHOUSE.LABEL'

  // Compact one-liner at every width (city/hood pages only). Same target, same event — just
  // one row instead of two tiles, so the results list starts ~150px higher.
  const compactRow = compact ? (
    <div className="border-t border-ink-100/15 bg-cream-300 px-4 py-2.5 md:px-6">
      <Link
        prefetch={false}
        href={otherHref}
        onClick={() => handleClick(target)}
        className="group flex items-center justify-between gap-3 text-[13px] leading-snug text-ink-200 transition-colors hover:text-blue-500"
      >
        <span className="min-w-0">
          {`${t('PILLAR_DISAMBIGUATION.COMPACT_PROMPT')} `}
          <span className="font-medium text-ink-700 group-hover:text-blue-500">
            {t(otherLabelKey)}
          </span>
          <span className="font-display text-blue-500 italic">
            {` · ${t(otherSizeKey)}`}
          </span>
        </span>
        <span
          aria-hidden="true"
          className="shrink-0 text-[15px] leading-none text-blue-500 transition-transform group-hover:translate-x-0.5"
        >
          →
        </span>
      </Link>
    </div>
  ) : null

  return (
    <>
      {compactRow}
      <section
        aria-label={t('PILLAR_DISAMBIGUATION.EYEBROW')}
        // `@container` — this strip renders inside the 45% results rail, so at
        // a 900px viewport its box is only ~357px. Viewport `md:` split that
        // into two ~170px tiles and the `truncate` on both labels ellipsized
        // them to "50 m²…" / "Bode…" — the disambiguation became unreadable
        // exactly where it has to do its job. See `OnDemandRecoveryCard`.
        className={`@container border-t border-ink-100/15 bg-cream-300 px-5 py-5 md:px-10 md:py-7 ${
          compact ? 'hidden' : ''
        }`}
      >
        <div className="mx-auto max-w-7xl">
          <p className="mb-3 font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:mb-4 md:text-xs">
            {t('PILLAR_DISAMBIGUATION.EYEBROW')}
          </p>

          <div className="grid grid-cols-1 gap-2 @md:grid-cols-2 @md:gap-3">
            {(['mini', 'warehouse'] as const).map((side) => {
              const isCurrent = PILLAR_BY_SIDE[side] === pillar
              const sizeKey =
                side === 'mini'
                  ? 'PILLAR_DISAMBIGUATION.MINI.SIZE'
                  : 'PILLAR_DISAMBIGUATION.WAREHOUSE.SIZE'
              const labelKey =
                side === 'mini'
                  ? 'PILLAR_DISAMBIGUATION.MINI.LABEL'
                  : 'PILLAR_DISAMBIGUATION.WAREHOUSE.LABEL'

              const tileBody = (
                <div className="flex min-w-0 flex-col gap-0.5">
                  <p className="truncate font-display text-[15px] leading-none font-medium tracking-[-0.01em] text-blue-500 italic md:text-[16px]">
                    {t(sizeKey)}
                  </p>
                  <p className="truncate font-display text-[16px] leading-tight font-medium text-ink-700 md:text-[18px]">
                    {t(labelKey)}
                  </p>
                </div>
              )

              if (isCurrent) {
                return (
                  <div
                    key={side}
                    aria-current="page"
                    className="flex items-center justify-between gap-3 rounded-lg border border-ink-100/15 bg-cream-300 px-4 py-3 md:px-5 md:py-4"
                  >
                    {tileBody}
                    {/* A state label, not a control (2026-09-26): as a white
                        pill with a blue border it drew 73 taps from 57 people
                        in 30 days. No chrome, no brand blue — blue is what
                        reads as tappable on this strip. */}
                    <span className="inline-flex shrink-0 items-center gap-1 font-display text-[10px] tracking-[0.18em] text-ink-200 uppercase">
                      <i
                        aria-hidden="true"
                        className="si si-check text-[10px] text-ink-200"
                      />
                      {t('PILLAR_DISAMBIGUATION.CURRENT_BADGE')}
                    </span>
                  </div>
                )
              }

              return (
                <Link
                  prefetch={false}
                  key={side}
                  href={otherHref}
                  onClick={() => handleClick(PILLAR_BY_SIDE[side])}
                  className="group flex items-center justify-between gap-3 rounded-lg border border-ink-100/15 bg-white-500 px-4 py-3 transition-colors hover:border-blue-500/40 md:px-5 md:py-4"
                >
                  {tileBody}
                  <span
                    aria-hidden="true"
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-500 text-[16px] leading-none text-white-500 transition-transform group-hover:translate-x-0.5 md:h-10 md:w-10"
                  >
                    →
                  </span>
                </Link>
              )
            })}
          </div>
        </div>
      </section>
    </>
  )
}

export default React.memo(PillarDisambiguation)
